import React, { useState } from "react";
import { MagnifyingGlass, CircleNotch, ArrowRight } from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { cardCls } from "@/components/admin/production/constants";
import { RUN_FIELDS } from "@/components/admin/rules/ruleText";

const IDENT = ["series", "edition", "length", "layout", "variant", "generation"];

function Trace({ trace, hubNames }) {
  if (!trace.length) return <div className="text-sm text-amber-300">No rules matched this van.</div>;
  return (
    <ol className="space-y-1.5" data-testid="van-preview-trace">
      {trace.map((t, i) => (
        <li key={i} className="text-xs flex gap-2">
          <span className="font-mono text-neutral-600 w-5 shrink-0">{i + 1}.</span>
          <span><span className="text-white">{t.title}</span>{t.changes.length > 0 && <span className="text-neutral-500"> → {t.changes.map((c) => `${c.field}: ${c.field === "hub" ? c.value : c.value ?? "—"}`).join(" · ")}</span>}</span>
        </li>
      ))}
    </ol>
  );
}

export function VanPreview() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setErr(""); setRes(null);
    try { const { data } = await adminApi.get(`/admin/rulebook/test/${encodeURIComponent(q.trim())}`); setRes(data); }
    catch (ex) { setErr(getErrorMessage(ex)); }
    finally { setBusy(false); }
  };
  const show = (k, v) => (k === "hub_type_id" ? res.hub_names[v] || "" : v);
  return (
    <div className={`${cardCls} p-6`} data-testid="van-preview">
      <div className="font-heading font-semibold text-white">Test a van</div>
      <p className="text-xs text-neutral-500 mt-1 mb-4">See exactly which rules run for a van, step by step, and what it ends up with.</p>
      <form onSubmit={run} className="flex items-center gap-2 bg-black/40 border border-neutral-700 rounded-full px-4 py-2.5 max-w-sm focus-within:border-[#73bf44] transition-colors">
        <MagnifyingGlass size={16} className="text-neutral-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Chassis e.g. OTM30526" data-testid="van-preview-input" className="bg-transparent outline-none text-sm text-white w-full font-mono placeholder:text-neutral-600" />
        {busy && <CircleNotch size={16} className="animate-spin text-neutral-500" />}
      </form>
      {err && <div className="text-sm text-amber-400 mt-3" data-testid="van-preview-error">{err}</div>}
      {res && (
        <div className="mt-5 grid lg:grid-cols-2 gap-6" data-testid="van-preview-result">
          <div className="space-y-3">
            <div className="text-xs text-neutral-500">{res.source === "schedule" ? "On the production schedule" : "Older van (database only)"} · code <span className="font-mono text-neutral-300">{res.start.model_code || "—"}</span> · PO {res.start.production_order ?? "—"}</div>
            <div className="flex flex-wrap gap-1.5">{IDENT.filter((k) => res.identity[k]).map((k) => <span key={k} className="text-[0.7rem] px-2 py-0.5 rounded-full border border-neutral-700 text-neutral-300">{k}: {res.identity[k]}</span>)}</div>
            {res.start.notes && <div className="text-xs text-neutral-400 border-l-2 border-amber-500/40 pl-3">Notes: {res.start.notes}</div>}
            <Trace trace={res.trace} hubNames={res.hub_names} />
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-800"><th className="py-2 pr-3 font-medium">Field</th><th className="py-2 pr-3 font-medium">After rules</th><th className="py-2 font-medium">In database</th></tr></thead>
            <tbody>
              {RUN_FIELDS.map(([k, l]) => {
                const after = show(k, res.fields[k]); const now = res.stored ? show(k, res.stored[k]) : null;
                const diff = res.stored && after && (after || "") !== (now || "");
                return (
                  <tr key={k} className={`border-b border-neutral-800/50 last:border-0 ${diff ? "bg-amber-500/[0.05]" : ""}`}>
                    <td className="py-2 pr-3 text-neutral-500 whitespace-nowrap">{l}</td>
                    <td className="py-2 pr-3 text-white">{after || "—"}</td>
                    <td className={`py-2 ${diff ? "text-amber-300" : "text-neutral-400"}`}>{res.stored ? now || "—" : <span className="text-neutral-600 inline-flex items-center gap-1">Not in database yet <ArrowRight size={11} /></span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
